'use client';

import React, { createContext, useContext, useState, useEffect } from 'react';

interface ScopeContextType {
    selectedScope: string;
    setScope: (newScope: string) => void;
    firstName: string;
    setFirstName: (newFirstName: string) => void;
}

export const ScopeContext = createContext<ScopeContextType>({
    selectedScope: '',
    setScope: () => {},
    firstName: '',
    setFirstName: () => {},
});

interface ScopeProviderProps {
    children: React.ReactNode;
}

const ScopeProvider: React.FC<ScopeProviderProps> = ({ children }) => {
    const [selectedScope, setSelectedScope] = useState<string>(() => {
        if (typeof window !== 'undefined') {
            return localStorage.getItem('selectedScope') || '';
        }
        return '';
    });

    const [firstName, setFirstNameState] = useState<string>(() => {
        if (typeof window !== 'undefined') {
            return localStorage.getItem('firstName') || '';
        }
        return '';
    });

    const setScope = (newScope: string) => {
        setSelectedScope(newScope);
        if (typeof window !== 'undefined') {
            localStorage.setItem('selectedScope', newScope);
        }
    };

    const setFirstName = (newFirstName: string) => {
        setFirstNameState(newFirstName);
        if (typeof window !== 'undefined') {
            localStorage.setItem('firstName', newFirstName);
        }
    };

    useEffect(() => {
        const handleStorageChange = (e: StorageEvent) => {
            if (e.key === 'selectedScope') {
                setSelectedScope(e.newValue || '');
            } else if (e.key === 'firstName') {
                setFirstNameState(e.newValue || '');
            }
        };

        window.addEventListener('storage', handleStorageChange);

        return () => {
            window.removeEventListener('storage', handleStorageChange);
        };
    }, []);

    return (
        <ScopeContext.Provider value={{ selectedScope, setScope, firstName, setFirstName }}>
            {children}
        </ScopeContext.Provider>
    );
};

const useScope = () => {
    const context = useContext(ScopeContext);
    if (!context) {
        throw new Error('useScope must be used within a ScopeProvider');
    }
    return context;
};

export { ScopeProvider, useScope };
